import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import BillingError from '@components/BillingError';
import { TextButton } from '@components/Button/variants';
import IconPlus from '@components/Images/Rebranding/IconPlus';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import Spinner from '@components/Spinner';
import { usePaymentMethodList } from '@hooks';
import useEventTracking from '@hooks/useEventTracking';
import { STRIPE_PUBLISH_KEY } from '@src/env';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { ClickTarget, Page, click } from '@tracking';
import { trackBillingErrorCta } from '@tracking/billingError';
import { createPastDuePaymentIntent, createSetupIntent } from '@utils/backend-api/stripe';
import { PaymentInfoProps } from '@utils/backend-api/updatePayment';
import { BillingErrorPayload } from '@utils/billingErrors';
import { PAYMENT_ELEMENT_APPEARANCE } from '@utils/constants';
import { apiErrorPayload } from '@utils/stripePaymentError';

import CancelAccount from './CancelAccount';
import NewPaymentMethod from './NewPaymentMethod';
import PastDueMessage from './PastDueMessage';
import PaymentMethod from './PaymentMethod';
import SubscriptionInfo from './SubscriptionInfo';
import { INTENT_TYPES, IntentType } from './constants';

const stripePromise = loadStripe(STRIPE_PUBLISH_KEY);

const MAX_CREDIT_CARD_ALLOWANCE = 3;

const UpdatePaymentInfo = ({
  billingAmount,
  billingDate,
  isUserPastDue,
  stripeCustomerId,
  currentPlanType,
}: PaymentInfoProps) => {
  const [clientSecret, setClientSecret] = useState('');
  const [intentType, setIntentType] = useState<IntentType>(INTENT_TYPES.Setup);
  const [amountDue, setAmountDue] = useState<number | undefined>();
  const [intentError, setIntentError] = useState<BillingErrorPayload | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const { paymentMethodList, isLoadingPaymentMethodList } = usePaymentMethodList();
  const [isAddingNewPaymentMethod, setIsAddingNewPaymentMethod] = useState(false);
  const { Track, trackEvent } = useEventTracking({
    page: Page.UpdatePage,
  });

  const paymentMethodUsageOverAllowance = paymentMethodList?.length >= MAX_CREDIT_CARD_ALLOWANCE;

  // Past-due users pay the past-due invoice's own PaymentIntent while saving the card, so wallets
  // show the real amount. Returns false when there's nothing to pay that way.
  const fetchPastDuePaymentIntent = async () => {
    try {
      const { clientSecret: secret, amount } = await createPastDuePaymentIntent();
      if (!secret) {
        return false;
      }
      setClientSecret(secret);
      setIntentType(INTENT_TYPES.Payment);
      setAmountDue(amount);
      return true;
    } catch {
      // Fall back to a SetupIntent: the backend still charges the past-due invoice off-session
      // once the new card is saved.
      return false;
    }
  };

  const fetchIntent = async () => {
    setIsCreatingIntent(true);
    setIntentError(null);
    try {
      if (isUserPastDue && (await fetchPastDuePaymentIntent())) {
        return;
      }
      const { clientSecret: secret } = await createSetupIntent({
        stripe_customer_id: stripeCustomerId,
      });
      setClientSecret(secret);
      setIntentType(INTENT_TYPES.Setup);
    } catch (error) {
      setIntentError(
        apiErrorPayload(
          error,
          'We could not start adding a payment method. Please refresh the page and try again.'
        )
      );
    } finally {
      setIsCreatingIntent(false);
    }
  };

  useEffect(() => {
    fetchIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeCustomerId, isUserPastDue]);

  const AddPaymentMethodBlock = () => {
    if (isAddingNewPaymentMethod) return <></>;

    if (!paymentMethodUsageOverAllowance) {
      return (
        <div className="py-32">
          <TextButton
            as="button"
            className={clsx(
              'font-suisse-intl text-button-lg text-grayscale-charcoal no-underline',
              {
                'text-grayscale-gray': paymentMethodUsageOverAllowance,
              }
            )}
            disabled={paymentMethodUsageOverAllowance}
            onClick={() => setIsAddingNewPaymentMethod(true)}
          >
            <IconPlus className="mr-8 h-20 w-20" />
            <span>Add payment method</span>
          </TextButton>
        </div>
      );
    } else {
      return (
        <p className="py-32 text-left text-body-md text-grayscale-charcoal">
          You’ve reached the maximum number of saved payment methods. If you need to add a new
          payment method, first remove one of the saved cards above before continuing.
        </p>
      );
    }
  };

  return (
    <Track>
      <div className="container" id="update-card">
        <div className="row">
          <div className="col-md-8 col-md-offset-2">
            <div className="subscription-info flex justify-center">
              {isUserPastDue ? (
                <PastDueMessage />
              ) : (
                <>
                  {billingDate && (
                    <SubscriptionInfo
                      billingAmount={billingAmount}
                      billingDate={billingDate}
                      currentPlanType={currentPlanType}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="row mt-16 flex flex-col items-center px-16 sm:px-0">
          {!isUserPastDue && (
            <SectionBreakTitle
              className="mb-32 w-full max-w-[624px]"
              color={SectionColor.Magenta}
              title="Payment methods"
            />
          )}
          <div className="w-full max-w-[624px]">
            {isLoadingPaymentMethodList ? (
              <div className="mb-16 mt-16 text-center">
                <Spinner />
              </div>
            ) : (
              <>
                {paymentMethodList.map((paymentMethod) => (
                  <Elements
                    stripe={stripePromise}
                    key={paymentMethod.id}
                    options={{ appearance: PAYMENT_ELEMENT_APPEARANCE }}
                  >
                    <PaymentMethod
                      paymentMethod={paymentMethod}
                      numberOfPaymentMethods={paymentMethodList.length}
                    />
                  </Elements>
                ))}
                {paymentMethodList.length <= 0 && (
                  <div className="text-center font-suisse-intl text-body-lg font-light">
                    No payment methods found.
                  </div>
                )}
                <AddPaymentMethodBlock />
              </>
            )}
            {isAddingNewPaymentMethod && (
              <div className="flex flex-col pt-32">
                <div className="flex justify-between">
                  <div className="font-suisse-intl text-button-lg text-grayscale-charcoal">
                    Add payment method
                  </div>
                  <IconPlus
                    className="ml-8 h-[26px] w-[26px] rotate-45 cursor-pointer hover:text-primary-magenta dark:hover:text-primary-pink"
                    onClick={() => setIsAddingNewPaymentMethod(false)}
                    onClickCapture={() =>
                      trackEvent(click({ clickTarget: ClickTarget.AddNewPaymentMethod }))
                    }
                  />
                </div>
                {intentError ? (
                  <div className="py-32 text-left text-body-md text-grayscale-charcoal">
                    <BillingError error={intentError} showCta={false} className="mb-16 mt-0" />
                    <TextButton
                      as="button"
                      onClick={() => {
                        trackBillingErrorCta(intentError, 'try_again', 'Try again');
                        fetchIntent();
                      }}
                    >
                      Try again
                    </TextButton>
                  </div>
                ) : isCreatingIntent || !clientSecret ? (
                  <div className="mb-16 mt-16 text-center">
                    <Spinner />
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret,
                        appearance: PAYMENT_ELEMENT_APPEARANCE,
                      }}
                    >
                      <NewPaymentMethod
                        stripeCustomerId={stripeCustomerId}
                        intentType={intentType}
                        amountDue={amountDue}
                      />
                    </Elements>
                  </div>
                )}
              </div>
            )}
          </div>
          <CancelAccount />
        </div>
      </div>
    </Track>
  );
};

export default UpdatePaymentInfo;
